/**
 * Demo-Fixture für die Stellungnahme-Generierung.
 *
 * Liefert eine vollständige `BuildPlaceholdersInput`-Struktur mit
 * synthetischen Beispieldaten — geeignet für den „Demo-Stellungnahme
 * generieren"-Button im Berater-Cockpit (AP8).
 *
 * Sobald echte Mandantendaten + Berater-Inputs persistiert werden (AP9+),
 * wird dieser Fixture-Generator durch einen echten Adapter ersetzt:
 * `buildInputFromCase(caseId, supabaseClient): Promise<BuildPlaceholdersInput>`.
 */

import type { statement } from '@tragfaehigkeit/core';

type BuildPlaceholdersInput = statement.BuildPlaceholdersInput;
type AssessmentTexts = statement.AssessmentTexts;

const ID = '5b3b3a1a-1a2b-4c3d-8e4f-0123456789ab';
const NOW = new Date('2026-05-06T10:00:00Z');
const SUBMISSION = new Date('2026-04-28T10:00:00Z');
const MEETING = new Date('2026-05-02T10:00:00Z');

const dimText = (short: string, detail: string): { short: string; detail: string } => ({
  short,
  detail,
});

const ASSESSMENTS: AssessmentTexts = {
  revenueAssessment: 'stabil über 12 Monate',
  costsAssessment: 'branchenüblich',
  profitAssessment: 'unter Bedarfsgrenze',
  hourlyRateAssessment: 'marktkonform für Branche',
  liquidityAssessment: 'ausreichend für 6 Wochen',
  runwayAssessment: 'unbegrenzt bei aktueller Auftragslage',
  utilizationAssessment: 'ausbaufähig',
  verdictLongParagraph:
    'Die Tragfähigkeit der selbständigen Tätigkeit von Frau Mustermann ist nach Auswertung der vorgelegten Unterlagen und des Beratungsgesprächs eingeschränkt gegeben. Der erwirtschaftete Gewinn deckt aktuell rund 80 % des Bedarfs der Bedarfsgemeinschaft, sodass eine teilweise ergänzende Hilfebedürftigkeit fortbesteht.',
  verdictReasoning:
    'Die Bewertung stützt sich auf eine sechs-dimensionale Analyse von Identität, Geschäftsmodell, Marktposition, Ressourcen, Gesundheit und privatem Umfeld. Identität und fachliche Eignung sind gut gegeben (Score 70). Die Geschäftsmodell-Skalierbarkeit ist eingeschränkt durch die persönliche Leistungserbringung. Die Ressourcenlage ist solide, aber die Auslastung ist mit aktuell rund 38 Stunden pro Monat unter dem branchenüblichen Mittel. Familienverantwortung mit zwei schulpflichtigen Kindern reduziert die Verfügbarkeit für eine Vollzeit-Vermittlung.',
  recommendationDetailed:
    '1. Fortführung der Selbständigkeit unter folgenden Auflagen: Re-Evaluation in 6 Monaten mit aktualisierter BWA. 2. Einreichung eines Liquiditätsplans für die nächsten 12 Monate. 3. Stundensatz-Überprüfung (Empfehlung: Anhebung um 10 % zur Branchen-Untergrenze). 4. Gespräch über Akquise-Strategie zur Auslastungs-Erhöhung.',
  conditionsParagraph:
    'Die Fortführung wird unter der Auflage einer Re-Evaluation nach 6 Monaten empfohlen.',
  reevaluationProposal:
    'Re-Evaluation am 6. November 2026 mit BWA Q2/Q3 2026 und aktualisiertem Auftragsbestand.',
  constraintDiscussion:
    'Familienverantwortung mit zwei schulpflichtigen Kindern (8 und 12 Jahre) begrenzt die zeitliche Verfügbarkeit für eine Vollzeit-Beschäftigung. Eine Vermittlung in eine Vollzeit-Stelle wäre aktuell unzumutbar im Sinne von § 10 Abs. 1 Nr. 3 SGB II.',
  riskIndicators:
    '· Auslastung 38 h/Monat liegt unter dem Branchenmittel von 80–100 h.\n· Stundensatz von 37 € liegt knapp unter der Branchen-Untergrenze von 40 €.\n· Bedarfsgemeinschaft mit hohem KdU (900 €/Monat) erschwert vollständige Bedarfsdeckung.',
  strengthIndicators:
    '· Stabile Auftragslage seit 14 Monaten ohne nennenswerte Schwankungen.\n· Positive Liquiditätsreichweite, kein erkennbares Klumpenrisiko.\n· Klare fachliche Qualifikation und langjährige Branchenerfahrung.\n· Etabliertes Kundennetzwerk.',
  dimensionTexts: {
    identity: dimText(
      'Solide Qualifikation und Branchenerfahrung',
      'Frau Mustermann verfügt über eine abgeschlossene Mediations-Ausbildung und 8 Jahre Berufserfahrung in einer Anwaltskanzlei vor der Selbständigkeit. Die fachliche Eignung für das Geschäftsmodell ist durchgehend dokumentiert.',
    ),
    activity: dimText(
      'Tragfähiges Geschäftsmodell, begrenzte Skalierbarkeit',
      'Das Geschäftsmodell „Mediation und Konfliktberatung für KMU" ist tragfähig und nachgefragt. Die Skalierbarkeit ist durch die persönliche Leistungserbringung begrenzt — Wachstum ist nur über Stundensatz-Erhöhung oder zusätzliche Berater möglich.',
    ),
    style: dimText(
      'Klare Marktposition, Pricing am unteren Rand',
      'Die Zielgruppe (KMU mit 5 bis 50 Mitarbeitenden) ist scharf umrissen. Die Differenzierung erfolgt über Branchen-Spezialisierung. Der Stundensatz von 37 € liegt knapp unter der Branchen-Untergrenze von 40 €; eine Anhebung wird empfohlen.',
    ),
    resources: dimText(
      'Solide Ressourcenlage, niedrige Auslastung',
      'Liquide Mittel von 2.100 € decken die Burn-Rate von 980 € für rund 8 Wochen. Bei aktuellem Cashflow ist die Reichweite faktisch unbegrenzt. Die Auslastung von 38 Stunden/Monat ist mit rund 24 % unter dem Branchenmittel und sollte schrittweise erhöht werden.',
    ),
    health: dimText(
      'Belastbar, ohne erkennbare Einschränkungen',
      'Die Selbsteinschätzung der Belastbarkeit ist gut, ohne erkennbare gesundheitliche Einschränkungen, die die Tragfähigkeit gefährden könnten. Pausen- und Erholungsstrategien sind etabliert.',
    ),
    private: dimText(
      'Familienverantwortung als Zumutbarkeitsgrund',
      'Bedarfsgemeinschaft mit Partner und zwei schulpflichtigen Kindern (8 und 12 Jahre). Die Kinderbetreuung ist nachmittags durch Hort gesichert, was eine flexible Arbeitszeit ermöglicht — eine Vollzeit-Vermittlung wäre jedoch unzumutbar.',
    ),
  },
};

export function buildDemoStatementInput(): BuildPlaceholdersInput {
  return {
    caseData: {
      id: ID as never,
      type: 'b2g',
      legalBasis: 'par10',
      userId: ID as never,
      status: 'data_collection',

      createdAt: NOW,
      updatedAt: NOW,
      jobcenterId: ID as never,
      jobcenterFileNumber: 'IFK-2026-12345',
    },
    client: {
      fullName: 'Maria Mustermann',
      birthDate: new Date('1978-03-15'),
      address: 'Hauptstr. 12, 80331 München',
    },
    business: {
      branch: 'Beratung und Coaching',
      legalForm: 'Einzelunternehmen',
      startDate: new Date('2024-01-01'),
      taxId: '143/123/45678',
      description: 'Mediation und Konfliktberatung für KMU',
    },
    score: {
      dimensionScores: [
        { dimension: 'identity', score: 70 as never, confidencePercent: 80 as never },
        { dimension: 'activity', score: 60 as never, confidencePercent: 75 as never },
        { dimension: 'style', score: 55 as never, confidencePercent: 70 as never },
        { dimension: 'resources', score: 65 as never, confidencePercent: 90 as never },
        { dimension: 'health', score: 75 as never, confidencePercent: 60 as never },
        { dimension: 'private', score: 50 as never, confidencePercent: 80 as never },
      ],
      totalScore: 62 as never,
      totalConfidencePercent: 76 as never,
      trafficLight: 'yellow',
      riskIndicators: [],
      computedAt: NOW,
      dimensionsScored: ['identity', 'activity', 'style', 'resources', 'health', 'private'],
      state: 'final',
    },
    financial: {
      id: ID as never,
      caseId: ID as never,
      monthlyAggregates: [
        { yearMonth: '2026-01', revenueCents: 240_000 as never, costsCents: 98_000 as never },
        { yearMonth: '2026-02', revenueCents: 240_000 as never, costsCents: 98_000 as never },
      ],
      hourlyRate: { billableHoursPerMonth: 38 as never },
      liquidity: {
        liquidAssetsCents: 210_000 as never,
        shortTermLiabilitiesCents: 0 as never,
        asOf: NOW,
      },
      bgComposition: {
        members: [
          { role: 'antragsteller', age: 47, specialNeeds: [] },
          { role: 'partner', age: 45, specialNeeds: [] },
          { role: 'kind', age: 8, specialNeeds: [] },
          { role: 'kind', age: 12, specialNeeds: [] },
        ],
        kdu: {
          kaltmiete: 70_000 as never,
          nebenkosten: 12_000 as never,
          heizkosten: 8_000 as never,
        },
      },
      sourceType: 'manual',
      uploadedFileNames: [],
      createdAt: NOW,
      updatedAt: NOW,
    },
    dimensions: [],
    provider: {
      name: 'Petschwork Consulting GmbH',
      addressLine1: 'Musterstr. 5',
      addressLine2: '81543 München',
      phoneEmail: 'Tel. 089 123456 · post@petschwork.de',
      certifications: 'AVGS § 45 SGB III · BAFA-zertifiziert',
      coachName: 'Christian Kopsch',
    },
    recipient: {
      jobcenterName: 'Jobcenter München Stadt',
      jobcenterAddressLine1: 'Kapuzinerstr. 26',
      jobcenterAddressLine2: '80337 München',
      jobcenterContact: 'Frau Schneider',
      jobcenterSalutation: 'Frau Schneider',
      jobcenterFileNumber: 'IFK-2026-12345',
      orderDate: new Date('2026-04-15'),
    },
    meeting: {
      statementDate: NOW,
      dataSubmissionDate: SUBMISSION,
      meetingDate: MEETING,
      meetingDurationMinutes: 60,
      meetingFormat: 'Videotermin',
      documentListBwa: 'BWA-Auswertung Q1 2025 bis Q1 2026 (DATEV)',
      documentListEks: 'Anlage EKS für 1. Mai 2025 bis 30. April 2026',
      documentListAdditional: 'Auftragsbestand Q2 2026, Liquiditätsplan',
    },
    assessments: ASSESSMENTS,
  };
}
